/*
 * er-pdp.js — Easy Rust "License Console" product page enhancement layer.
 *
 * Progressive enhancement ONLY. It listens to the same <input type="radio">
 * `change` events Dawn's product-info.js already uses (never replacing Dawn's
 * listener), and mirrors state into the custom console UI. With JS off, the
 * Liquid-rendered values stay authoritative and nothing breaks.
 *
 * Dawn replaces the whole product section's innerHTML on every variant change,
 * so this file is built to survive that:
 *   - a module init guard (window.__erPdpInit),
 *   - event delegation on `document` for radio changes (survives re-render),
 *   - idempotent DOM sync keyed off `data-er-*` flags,
 *   - a MutationObserver that re-syncs after Dawn swaps the section.
 *
 * Phase 0: plumbing + guards only. Phase 2 adds the Live Order Summary price
 * odometer + stock sync; Phase 5 adds the session-gated console boot.
 */
(function () {
  'use strict';
  if (window.__erPdpInit) return;
  window.__erPdpInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var DAYS = { '1 Day Key': 1, '3 Day Key': 3, '1 Week Key': 7, '1 Month Key': 30, '1 Year Key': 365 };
  var lastVid = null; // guards against re-entrancy from our own DOM writes / Dawn's re-render
  var rafId = null;

  // Read the variant JSON dumped by main-product.liquid (#ER-Variants-<sectionId>).
  function readVariants() {
    var el = document.querySelector('[id^="ER-Variants-"]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function money(cents) { return '$' + (Math.round(cents) / 100).toFixed(2); }

  function findVariant(variants, name) {
    for (var i = 0; i < variants.length; i++) { if (variants[i].option1 === name) return variants[i]; }
    return null;
  }

  // Odometer roll on the summary price. Skipped (instant) for equal values or reduced motion.
  function rollPrice(el, from, to) {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    if (reduceMotion || from === to) { el.textContent = money(to); return; }
    var start = null, dur = 420;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      el.textContent = money(from + (to - from) * eased);
      if (p < 1) rafId = requestAnimationFrame(step);
      else { el.textContent = money(to); rafId = null; }
    }
    rafId = requestAnimationFrame(step);
  }

  // Mirror the selected plan into the Live Order Summary. Runs exactly once per
  // real variant change (lastVid guard) so MutationObserver bursts + our own text
  // writes can never loop, and Dawn's post-fetch re-render (already correct) is a no-op.
  function sync() {
    var root = document.querySelector('.er-pdp');
    if (!root) return;
    var summary = root.querySelector('[data-er-summary]');
    var checked = root.querySelector('.product-form__input--pill input[type="radio"]:checked');
    if (!summary || !checked) return;
    var v = findVariant(readVariants(), checked.value);
    if (!v) return;
    if (v.id === lastVid) return;
    lastVid = v.id;

    var name = checked.value;
    var selEl = summary.querySelector('[data-er-sel]');
    if (selEl) selEl.textContent = name;

    var priceEl = summary.querySelector('[data-er-price]');
    if (priceEl) {
      var from = parseInt(priceEl.getAttribute('data-cents'), 10);
      if (isNaN(from)) from = v.price;
      priceEl.setAttribute('data-cents', v.price);
      rollPrice(priceEl, from, v.price);
    }

    var days = DAYS[name];
    var perEl = summary.querySelector('[data-er-perday]');
    if (perEl) {
      if (name === 'Lifetime Key') perEl.innerHTML = 'one-time &middot; never expires';
      else if (days) perEl.textContent = money(v.price / days) + ' / day';
      else perEl.textContent = '';
    }

    // Savings = the real dollar markdown off list (compare-at); hidden when none.
    // (Kept in sync with er-order-summary.liquid — dollar-only, never "$0.00".)
    var saveEl = summary.querySelector('[data-er-save]');
    if (saveEl) {
      var amt = (v.compare_at_price && v.compare_at_price > v.price) ? (v.compare_at_price - v.price) : 0;
      if (amt > 0) {
        saveEl.classList.remove('is-hidden');
        saveEl.textContent = 'Save ' + money(amt);
      } else {
        saveEl.classList.add('is-hidden');
      }
    }

    // Mirror into the mobile buy-bar (lives outside .er-pdp).
    var bb = document.querySelector('[data-er-buybar]');
    if (bb) {
      var bbName = bb.querySelector('[data-er-bb-name]');
      var bbPrice = bb.querySelector('[data-er-bb-price]');
      if (bbName) bbName.textContent = name;
      if (bbPrice) { bbPrice.textContent = money(v.price); bbPrice.setAttribute('data-cents', v.price); }
    }
  }

  // Mobile sticky buy-bar: forward-click to the real add button + reveal on scroll.
  function wireBuybar() {
    var bb = document.querySelector('[data-er-buybar]');
    if (!bb) return;
    var buyBtn = bb.querySelector('[data-er-bb-buy]');
    if (buyBtn && !buyBtn.dataset.erWired) {
      buyBtn.dataset.erWired = '1';
      buyBtn.addEventListener('click', function () {
        var real = document.querySelector('.er-pdp .product-form__submit') ||
          document.querySelector('.er-pdp [name="add"]');
        if (real) real.click();
      });
    }
    var cta = document.querySelector('.er-pdp .product-form__submit') ||
      document.querySelector('.er-pdp .cart__ctas');
    if (cta && !bb.dataset.erObserved) {
      bb.dataset.erObserved = '1';
      // Scroll-based (not IntersectionObserver): reliable even on jump-scrolls,
      // which can skip an IO threshold crossing entirely. Reveal once the inline
      // CTA has scrolled above the viewport top.
      var ticking = false;
      var updateBar = function () {
        var r = cta.getBoundingClientRect();
        var show = r.bottom < 8;
        bb.classList.toggle('is-visible', show);
        bb.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      var scheduleBarUpdate = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { updateBar(); ticking = false; });
      };
      window.addEventListener('scroll', scheduleBarUpdate, { passive: true });
      // ER perf: resize was unthrottled + non-passive; route it through the same
      // rAF tick as scroll so it coalesces to one measurement per frame (identical result).
      window.addEventListener('resize', scheduleBarUpdate, { passive: true });
      updateBar();
    }
  }

  // Subtle cursor-follow 3D tilt + glow-reveal on the community/Discord card.
  // "Look toward the cursor" parallax (pointer right → turn right, up → tilt up),
  // very slight (±5°). Idempotent, fine-pointer + motion-safe only.
  function wireCommunityTilt() {
    if (reduceMotion) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    var card = document.querySelector('.er-pdp .er-community');
    if (!card || card.dataset.erTilt) return;
    card.dataset.erTilt = '1';
    var MAX = 5, raf = null, ry = 0, rx = 0;
    function apply() {
      raf = null;
      card.style.setProperty('--er-comm-ry', ry.toFixed(2) + 'deg');
      card.style.setProperty('--er-comm-rx', rx.toFixed(2) + 'deg');
    }
    card.addEventListener('pointermove', function (e) {
      var r = card.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width;
      var py = (e.clientY - r.top) / r.height;
      ry = (px - 0.5) * 2 * MAX;
      rx = (py - 0.5) * -2 * MAX;
      card.classList.add('is-hover');
      if (!raf) raf = requestAnimationFrame(apply);
    });
    card.addEventListener('pointerleave', function () {
      card.classList.remove('is-hover');
      ry = 0; rx = 0;
      card.style.setProperty('--er-comm-ry', '0deg');
      card.style.setProperty('--er-comm-rx', '0deg');
    });
  }

  // Radio changes bubble; delegate so we keep working after Dawn re-renders.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.tagName === 'INPUT' && t.type === 'radio' && t.closest('.er-pdp')) {
      sync();
    }
  });

  // Re-sync after Dawn swaps the section innerHTML on variant change (debounced).
  var syncT = null;
  function debouncedSync() {
    if (syncT) clearTimeout(syncT);
    syncT = setTimeout(sync, 60);
  }
  function observe() {
    var host = document.querySelector('.er-pdp') || document.getElementById('MainContent');
    if (!host || !window.MutationObserver) return;
    var mo = new MutationObserver(debouncedSync);
    mo.observe(host, { childList: true, subtree: true });
  }

  function init() { sync(); observe(); wireBuybar(); wireCommunityTilt(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for later phases / debugging without re-scoping.
  window.__erPdp = { sync: sync, readVariants: readVariants, reduceMotion: reduceMotion };
})();
